import { parsePhoneNumber } from "libphonenumber-js";
import {
  AlertTriangle,
  ArrowDownToLine,
  ArrowUpFromLine,
  CircleDollarSign,
  Lock,
  LockOpen,
  RefreshCcw,
  Undo2,
  XCircle,
  type LucideIcon,
} from "lucide-react";
import { useMemo, useState } from "react";

import { useAuth } from "@/modules/auth/hooks/useAuth";
import { ColocaAlertDialog } from "@/shared/components/feedback/ColocaAlertDialog";
import { NoAccessView } from "@/shared/components/NoAccessView";
import { NotificationPhoneField } from "@/shared/components/notifications/NotificationPhoneField";
import { NotificationTypeCard } from "@/shared/components/notifications/NotificationTypeCard";
import { ProductNotificationsLayout } from "@/shared/components/notifications/ProductNotificationsLayout";
import { renderNotificationBubbleBody } from "@/shared/components/notifications/renderNotificationBubbleBody";
import { WhatsAppBubble } from "@/shared/components/notifications/WhatsAppBubble";
import { WhatsAppIcon } from "@/shared/components/notifications/WhatsAppIcon";
import type { CardsTranslations } from "@/shared/lang/interfaces/cards";
import { useTranslations } from "@/shared/lang/useTranslations";
import { cn } from "@/shared/lib/utils";

import { useCardNotificationPreferences } from "../hooks/useCardNotificationPreferences";
import type { CardNotificationEvent } from "../services/cardNotifications.service";

type CardsKey = Extract<keyof CardsTranslations, `notifications_${string}`>;

interface CardNotificationDefinition {
  event: CardNotificationEvent;
  icon: LucideIcon;
  iconBg: string;
  nameKey: CardsKey;
  descKey: CardsKey;
  whenKey: CardsKey;
  bodyKey: CardsKey;
  /** The recharge template predates the titled ones. */
  titleKey?: CardsKey;
  /** The recharge template predates the current ones and has no footer. */
  footer?: boolean;
  optIn?: boolean;
}

const CARD_NOTIFICATIONS: CardNotificationDefinition[] = [
  {
    event: "recharge",
    icon: ArrowDownToLine,
    iconBg: "#00735C",
    nameKey: "notifications_topup_name",
    descKey: "notifications_topup_desc",
    whenKey: "notifications_topup_when",
    bodyKey: "notifications_bubble_topup_body",
    footer: false,
  },
  {
    event: "transaction_declined",
    icon: XCircle,
    iconBg: "oklch(0.55 0.18 25)",
    nameKey: "notifications_declined_name",
    descKey: "notifications_declined_desc",
    whenKey: "notifications_declined_when",
    bodyKey: "notifications_bubble_declined_body",
    titleKey: "notifications_bubble_declined_title",
  },
  {
    event: "transaction_reversed",
    icon: Undo2,
    iconBg: "oklch(0.58 0.12 220)",
    nameKey: "notifications_reversed_name",
    descKey: "notifications_reversed_desc",
    whenKey: "notifications_reversed_when",
    bodyKey: "notifications_bubble_reversed_body",
    titleKey: "notifications_bubble_reversed_title",
  },
  {
    event: "refund_received",
    icon: RefreshCcw,
    iconBg: "oklch(0.6 0.13 175)",
    nameKey: "notifications_refund_name",
    descKey: "notifications_refund_desc",
    whenKey: "notifications_refund_when",
    bodyKey: "notifications_bubble_refund_body",
    titleKey: "notifications_bubble_refund_title",
  },
  {
    event: "blocked",
    icon: Lock,
    iconBg: "oklch(0.5 0.12 30)",
    nameKey: "notifications_blocked_name",
    descKey: "notifications_blocked_desc",
    whenKey: "notifications_blocked_when",
    bodyKey: "notifications_bubble_blocked_body",
    titleKey: "notifications_bubble_blocked_title",
  },
  {
    event: "unblocked",
    icon: LockOpen,
    iconBg: "oklch(0.6 0.13 175)",
    nameKey: "notifications_unblocked_name",
    descKey: "notifications_unblocked_desc",
    whenKey: "notifications_unblocked_when",
    bodyKey: "notifications_bubble_unblocked_body",
    titleKey: "notifications_bubble_unblocked_title",
  },
  {
    event: "withdrawal",
    icon: ArrowUpFromLine,
    iconBg: "oklch(0.55 0.12 250)",
    nameKey: "notifications_withdrawal_name",
    descKey: "notifications_withdrawal_desc",
    whenKey: "notifications_withdrawal_when",
    bodyKey: "notifications_bubble_withdrawal_body",
    titleKey: "notifications_bubble_withdrawal_title",
  },
  {
    event: "balance_low",
    icon: AlertTriangle,
    iconBg: "oklch(0.65 0.16 75)",
    nameKey: "notifications_balance_low_name",
    descKey: "notifications_balance_low_desc",
    whenKey: "notifications_balance_low_when",
    bodyKey: "notifications_bubble_balance_low_body",
    titleKey: "notifications_bubble_balance_low_title",
  },
  {
    event: "transaction_approved",
    icon: CircleDollarSign,
    iconBg: "oklch(0.55 0.15 270)",
    nameKey: "notifications_approved_name",
    descKey: "notifications_approved_desc",
    whenKey: "notifications_approved_when",
    bodyKey: "notifications_bubble_approved_body",
    titleKey: "notifications_bubble_approved_title",
    optIn: true,
  },
];

/**
 * WhatsApp alerts Coloca sends to the CARDHOLDER — the one product whose notifications are live end to end.
 *
 * It used to be a section of general Settings, behind a drawer. It lives here because the preference belongs
 * to the product it configures, and because a route can be linked to and a drawer cannot.
 */
export const CardNotificationsPage = () => {
  const { t } = useTranslations("cards");
  const { t: tShared } = useTranslations("notificationPreferences");
  const { t: tGlobal } = useTranslations("global");
  const { user } = useAuth();

  const {
    prefs,
    hasNoAccess,
    canWrite,
    controlsDisabled,
    activeCount,
    totalTypes,
    pendingToggle,
    setPendingToggle,
    confirmPendingToggle,
    handleToggle,
    thresholdInput,
    thresholdError,
    handleThresholdChange,
    handleThresholdBlur,
    sendTest,
  } = useCardNotificationPreferences();
  const [testPhone, setTestPhone] = useState("");
  const testRecipient = testPhone ? parsePhoneNumber(testPhone).formatInternational() : "";

  const breadcrumb = useMemo(
    () => [
      { label: tGlobal("sidebar_menu_cards"), to: "/app/cards/cards" },
      { label: t("notifications_page_title") },
    ],
    [t, tGlobal],
  );

  const pending = CARD_NOTIFICATIONS.find((definition) => definition.event === pendingToggle);

  if (hasNoAccess) return <NoAccessView />;

  const thresholdField = (
    <div className="flex flex-col gap-1.5">
      <label
        htmlFor="card-balance-low-threshold"
        className="font-mono text-[10px] uppercase tracking-[0.08em] text-muted-foreground"
      >
        {t("notifications_balance_low_threshold_label")}
      </label>
      <div className="flex items-center gap-2">
        <span className="font-mono text-[12px] text-muted-foreground">
          {t("notifications_balance_low_threshold_prefix")}
        </span>
        <input
          id="card-balance-low-threshold"
          type="number"
          inputMode="decimal"
          min={0}
          step="0.01"
          value={thresholdInput}
          onChange={(e) => handleThresholdChange(e.target.value)}
          onBlur={handleThresholdBlur}
          disabled={controlsDisabled}
          placeholder="25.00"
          className={cn(
            "h-9 w-32 rounded-md border bg-background px-2.5 font-mono text-[13px]",
            thresholdError ? "border-destructive" : "border-border",
          )}
        />
      </div>
      {thresholdError ? (
        <p className="text-[11px] text-destructive">{thresholdError}</p>
      ) : (
        <p className="text-[11px] text-muted-foreground">{t("notifications_balance_low_threshold_help")}</p>
      )}
    </div>
  );

  return (
    <ProductNotificationsLayout
      breadcrumb={breadcrumb}
      eyebrow={t("notifications_eyebrow")}
      title={t("notifications_page_title")}
      subtitle={t("notifications_page_subtitle")}
      heroTitle={t("notifications_hero_title")}
      heroSub={t("notifications_hero_sub")}
      heroAside={
        <div className="md:text-right">
          <div className="font-mono text-[9.5px] uppercase tracking-[0.06em] text-muted-foreground">
            {tShared("active_types")}
          </div>
          <div className="font-mono text-[18px] font-bold tabular-nums">
            {activeCount}/{totalTypes}
          </div>
        </div>
      }
      controls={
        canWrite ? (
          <div className="md:max-w-[360px]">
            <NotificationPhoneField
              label={tShared("test_phone_label")}
              description={tShared("test_phone_help")}
              defaultCountry={user?.country ?? undefined}
              onChange={(phone) => setTestPhone(phone ?? "")}
            />
          </div>
        ) : undefined
      }
      footnote={t("notifications_footnote")}
    >
      <div className="flex flex-col gap-3">
        {CARD_NOTIFICATIONS.map((definition) => (
          <NotificationTypeCard
            key={definition.event}
            icon={definition.icon}
            iconBg={definition.iconBg}
            name={t(definition.nameKey)}
            desc={t(definition.descKey)}
            optInTag={definition.optIn ? t("notifications_approved_opt_in_tag") : undefined}
            enabled={prefs[definition.event]}
            disabled={controlsDisabled}
            onToggle={handleToggle(definition.event)}
            onSendTest={canWrite ? () => sendTest(definition.event)(testPhone) : undefined}
            testRecipient={testRecipient}
            belowBody={definition.event === "balance_low" ? thresholdField : undefined}
            bubble={
              <WhatsAppBubble
                header={definition.titleKey ? t(definition.titleKey) : undefined}
                body={renderNotificationBubbleBody(t(definition.bodyKey))}
                footer={definition.footer === false ? undefined : tShared("preview_footer")}
                recipientHint={t("notifications_bubble_recipient_hint")}
              />
            }
          />
        ))}
      </div>

      {/* Switching one ON starts messaging real cardholders, so the gesture is confirmed first. */}
      <ColocaAlertDialog
        trigger={<span style={{ display: "none" }} />}
        open={pending !== undefined}
        onOpenChange={(open) => {
          if (!open) setPendingToggle(null);
        }}
        media={<WhatsAppIcon size={28} />}
        title={pending ? t("notifications_enable_confirm_title", { name: t(pending.nameKey) }) : ""}
        description={
          <span className="block space-y-2 text-left">
            <span className="block">{t("notifications_enable_confirm_intro")}</span>
            <span className="block rounded-md border border-border bg-muted/50 px-3 py-2 text-[12px] text-foreground">
              <span className="block font-semibold">{t("notifications_enable_confirm_who_title")}</span>
              <span className="block text-muted-foreground">
                {t("notifications_enable_confirm_who_body")}
              </span>
              <span className="mt-2 block font-semibold">{t("notifications_enable_confirm_when_title")}</span>
              <span className="block text-muted-foreground">{pending ? t(pending.whenKey) : ""}</span>
            </span>
            <span className="block text-[11.5px] text-muted-foreground">
              {t("notifications_enable_confirm_reversible")}
            </span>
          </span>
        }
        confirmLabel={t("notifications_enable_confirm_button")}
        cancelLabel={t("notifications_enable_confirm_cancel")}
        confirmVariant="primary"
        onConfirm={confirmPendingToggle}
        onCancel={() => setPendingToggle(null)}
      />
    </ProductNotificationsLayout>
  );
};
